
/**
 * Utility to detect if the app is running inside various webviews
 */

// Define types for webkit message handlers
interface WebKitMessageHandlers {
  goNative?: {
    postMessage: (message: any) => void;
  };
  oauth?: {
    postMessage: (message: any) => void;
  };
}

// Augment Window interface to include webkit
declare global {
  interface Window {
    webkit?: {
      messageHandlers?: WebKitMessageHandlers;
    };
    gonative?: any;
    median?: any;
    // Ajouter une propriété personnalisée pour suivre l'état d'authentification
    googleAuthCompleted?: boolean;
  }
}

/**
 * Detects if the current environment is a Median/GoNative webview
 */
export const isMedianWebview = (): boolean => {
  // Check for Median/GoNative specific user agent patterns or window properties
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Look for GoNative or Median specific indicators
  if (userAgent.includes('gonative') || 
      userAgent.includes('median') || 
      (window as any).gonative || 
      (window as any).median) {
    return true;
  }
  
  // Check for common webview indicators
  const isWebView = 
    userAgent.includes('wv') || 
    userAgent.includes('webview') ||
    // iOS webview detection
    (userAgent.includes('iphone') || userAgent.includes('ipad')) && 
    !userAgent.includes('safari') && userAgent.includes('applewebkit') ||
    // Generic Android webview detection
    (userAgent.includes('android') && userAgent.includes('wv'));
  
  return isWebView;
};

/**
 * Detects if Google Sign-In is likely to be blocked
 * This covers Median/GoNative and other webviews that might block Google auth
 */
export const isGoogleSignInBlocked = (): boolean => {
  // Median/GoNative webviews block Google Sign-In
  if (isMedianWebview()) return true;
  
  // Check if we're in any type of webview that might block third-party cookies or popups
  const userAgent = navigator.userAgent.toLowerCase();
  const isGenericWebView = 
    userAgent.includes('wv') || 
    userAgent.includes('webview') ||
    // Check for third-party cookie blocking
    !navigator.cookieEnabled ||
    // Check if popups might be blocked (not foolproof but helpful)
    typeof window.open === 'undefined';
    
  return isGenericWebView;
};

export const configureWebviewForAuth = () => {
  // Configure deep linking for the webview
  if (window.webkit?.messageHandlers?.goNative) {
    window.webkit.messageHandlers.goNative.postMessage({
      command: 'register',
      urls: ['com.sportlogin.app://*']
    });
  }
  
  // Handle OAuth redirects
  if (window.webkit?.messageHandlers?.oauth) {
    window.webkit.messageHandlers.oauth.postMessage({
      command: 'register',
      scheme: 'com.sportlogin.app'
    });
    
    window.addEventListener('message', (event) => {
      if (event.data.type === 'oauth' && event.data.token) {
        window.postMessage({ type: 'GOOGLE_AUTH', token: event.data.token }, window.location.origin);
        window.googleAuthCompleted = true;
      }
    });
  }
  
  // Écouter les événements URL pour la redirection depuis Google OAuth
  document.addEventListener('DOMContentLoaded', () => {
    // Vérifier si nous avons été redirigés avec un code ou token OAuth
    const urlParams = new URLSearchParams(window.location.search);
    const oauthCode = urlParams.get('code');
    const oauthToken = urlParams.get('token');
    
    if (oauthCode || oauthToken) {
      const tokenToUse = oauthToken || oauthCode;
      console.log('OAuth redirect detected with code/token:', tokenToUse);
      
      // Notifier l'application du token/code reçu
      window.postMessage({ 
        type: 'GOOGLE_AUTH', 
        token: tokenToUse 
      }, window.location.origin);
      
      // Marquer l'authentification comme complétée
      window.googleAuthCompleted = true;
    }
  });
};
